import React from 'react';
import { Scroll, Award, Sparkles, BookOpen, Globe2 } from 'lucide-react';

export default function CultureSection() {
  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-10">
      
      {/* Title */}
      <div className="text-center space-y-3 border-b border-gray-200 pb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-50 text-[#8B0000] border border-red-200 text-xs font-semibold">
          <BookOpen className="w-3.5 h-3.5" />
          Culture & History 媽祖文化和歷史
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-[#8B0000] font-serif">
          天后媽祖簡介與信仰歷史
        </h1>
        <p className="text-gray-500 font-mono text-sm">
          A 1,000-Year Heritage of Benevolence, Compassion & Peace
        </p>
      </div>

      {/* Main Biography Article */}
      <article className="bg-white border border-gray-200 rounded-2xl p-6 md:p-10 shadow-sm space-y-6">
        <div className="flex items-center gap-3 border-b border-gray-200 pb-3">
          <Scroll className="w-7 h-7 text-[#8B0000]" />
          <h2 className="text-2xl font-bold text-[#8B0000] font-serif">
            天后媽祖生平與顯靈事蹟
          </h2>
        </div>

        <div className="space-y-4 text-gray-800 text-base sm:text-lg leading-relaxed font-serif">
          <p>
            天后媽祖生於北宋太宗建隆元年（960）農曆三月二十三日，卒於太宗雍熙四年（987）二月二十九日，是福建莆田湄洲郡巡檢林願的第六個女兒，原名林湄娘，又名林默。自幼聰穎靈悟，成人後識天文，懂醫理，樂於助人，屢次在海上救助遇難之人，還為老百姓驅逐瘟疫，治病救人，深受人們的愛戴和崇敬。人們為感謝其恩德，稱她為“媽祖”，福建話是“母親”的意思。媽祖世稱默娘，娘媽，被奉祀為“護航海神”。
          </p>

          <p>
            年僅28歲的媽祖羽化升天以後，多次顯靈救助苦難。人們在遇到困難時只要求聲“媽祖保佑”，媽祖就會聞聲而至，人們就能逢凶化吉，遇難呈祥。莆田人最初供奉媽祖的廟宇稱為神女廟，神女祠，得到信仰者廣泛熱烈的信奉。鄭和下西洋時，因媽祖庇護有功，奉旨遣官修整祠廟。到清乾隆以後，湄洲媽祖祖廟已頗具規模，成為一座有99間齋房，號稱“海上龍宮”的雄偉建築群。
          </p>

          <p>
            歷代王朝也順應民心支持媽祖信仰。宋代封媽祖為“夫人”；元明兩朝加封為“天妃”；清朝晉封為“天后”。歷代皇帝對媽祖進行了30多次的褒封，其爵位從“夫人”，“妃”，直至“天妃”，“天后”，並被人們尊稱為“天上聖母”。
          </p>

          <div className="bg-amber-50/80 border-l-4 border-[#8B0000] p-4 rounded-r-xl my-4 text-gray-800 font-sans text-sm sm:text-base space-y-2">
            <div className="flex items-center gap-2 font-bold text-[#8B0000]">
              <Award className="w-5 h-5 text-[#8B0000]" />
              <span>聯合國與國際榮譽 (International Recognition)</span>
            </div>
            <p>
              二十世紀八十年代聯合國授予中國媽祖“和平女神”的稱號。2009年媽祖文化已被聯合國教科文組織列為人類非物質文化遺產（信俗類）。目前，全世界媽祖廟有5000多座，莆田湄洲媽祖祖廟是世界始建年代最早，建設規模最大，祭拜規格最高的媽祖廟，信眾2億多人。
            </p>
          </div>

          <p>
            美國媽祖基金會成立於2014年，是以弘揚媽祖文化精神為宗旨的公益組織，致力於傳承媽祖信仰從產生至今經歷了1055年的立德，行善，博愛的媽祖文化美德，讓媽祖在大洋彼岸普照信眾。媽祖文化是真善美的化身。她告訴世界人民，中國人民希望和諧，和睦，和平。媽祖文化已成為中華民族乃至世界人民的精神食糧。
          </p>
        </div>
      </article>

      {/* Special Feature: Paper-Sculpture Mazu in Germany */}
      <article className="bg-white border border-gray-200 rounded-2xl p-6 md:p-10 shadow-sm space-y-6">
        <div className="flex items-center gap-3 border-b border-gray-200 pb-4">
          <div className="p-2.5 rounded-lg bg-red-50 text-[#8B0000] border border-red-200">
            <Globe2 className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#8B0000] font-serif">
              媽祖神尊德國展神威
            </h2>
            <p className="text-xs text-gray-500 font-mono">
              German Berlin International Craft Exhibition Feature
            </p>
          </div>
        </div>

        <div className="space-y-4 text-gray-700 text-sm sm:text-base leading-relaxed">
          <p>
            雲林縣北港鎮紙塑工藝師蔡爾容，卜雅菡夫妻，以古法製作具有筋，骨，肉，皮的“紙塑”媽祖神尊，有別於一般的“紙糊”工法，極具特色，獲邀參加“德國柏林國際工藝展”。二人將藉此機會在歐洲宣揚媽祖宗教信仰文化。
          </p>

          <p>
            據《中國時報》報導，蔡爾容傳承家族50餘年的紙藝工夫，一開始以“紙糊”工法製作神尊，後得知中國有失傳百年的“紙塑”工法，耗時20餘年蒐集資料，從古書的隻字片語中加以推敲，終於重現古老工法，吸引新加坡祠廟前來訂制，亦受邀參展中國文創博覽會。
          </p>

          <p>
            蔡爾容是台灣唯一獲邀參展“德國柏林國際工藝展”的工藝師。北港朝天宮與北港媽祖文教基金會贊助了其前往德國的經費，使蔡爾容德國之行順利成行。
          </p>

          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 space-y-2">
            <h4 className="text-[#8B0000] font-bold font-serif flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#B8860B]" />
              一紙，二土，三木，四金銅 - 古法“紙塑”工藝
            </h4>
            <p className="text-xs sm:text-sm text-gray-600">
              蔡爾容說，“紙糊”神尊是以薄紙片製作，“紙塑”神尊則是用一層層的竹子紙堆疊而成的“軟身”神尊，兼具柔軟與堅韌，具有筋，肉，骨，皮。若維護得宜，可保存百年以上不毀壞。蔡爾容指出，製作神尊有各種制材，相稱“一紙，二土，三木，四金銅”，就是指紙塑神尊最為神靈。
            </p>
          </div>
        </div>
      </article>

    </div>
  );
}
